import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { createLogger } from '../../../../utils/logger';
const logger = createLogger('TributeDonationsData');

import {
  TributeDonationsRec,
  tributeDonationsService,
} from '../../../../services/tributeDonationsInfo';

type RouteParams = {
  tributeDonationsId?: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type TributeDonationsDataScreenRouteProp = RouteProp<RootStackParamList, 'TributeDonationsData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const TributeDonationsDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<TributeDonationsDataScreenRouteProp>();
  const [tributeDonationsInfo, setTributeDonationsInfo] = useState<TributeDonationsRec | null>(null);
  const [loading, setLoading] = useState(true);

  const tributeDonationsId = route.params?.tributeDonationsId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly;

  useEffect(() => {
    loadTributeDonationsInfo();
  }, []);

  const loadTributeDonationsInfo = async () => {
    try {
      if (tributeDonationsId) {
        const info = await tributeDonationsService.getTributeDonationsById(tributeDonationsId, targetUserId);
        setTributeDonationsInfo(info);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await tributeDonationsService.getAllTributeDonations(targetUserId);
        if (allInfo.length > 0) {
          setTributeDonationsInfo(allInfo[0]);
        }
      }
    } catch (error) {
      logger.error('Error loading tribute donations info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (tributeDonationsInfo) {
      // Map the data from tributeDonationsInfo to the format expected by TributeDonationsInputScreen
      const mappedData = {
        id: tributeDonationsInfo.id,
        tributeType: tributeDonationsInfo.tributeType,
        tributeDetails: tributeDonationsInfo.tributeDetails,
        specialInstructions: tributeDonationsInfo.specialInstructions,
        notes: tributeDonationsInfo.notes,
        estimatedCost: tributeDonationsInfo.estimatedCost,
        contactName: tributeDonationsInfo.contactName,
        contactPhone: tributeDonationsInfo.contactPhone,
        contactEmail: tributeDonationsInfo.contactEmail,
        organizationName: tributeDonationsInfo.organizationName,
      };

      navigation.navigate('TributeDonationsInput', {
        tributeDonationsData: mappedData,
        isEditing: true
      });
    } else {
      navigation.navigate('TributeDonationsInput');
    }
  };

  const handleDelete = () => {
    if (tributeDonationsInfo) {
      Alert.alert(
        'Delete Tribute Donations',
        'Are you sure you want to delete this tribute donations information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                await tributeDonationsService.deleteTributeDonations(tributeDonationsInfo.id);
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting tribute donations info:', error);
                Alert.alert('Error', 'Failed to delete tribute donations information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Tribute Donations Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Tribute Donations Details</Text>
      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {tributeDonationsInfo ? (
            <>
              <InfoItem
                label="Tribute Type"
                value={tributeDonationsInfo.tributeType ?
                  tributeDonationsInfo.tributeType.charAt(0).toUpperCase() +
                  tributeDonationsInfo.tributeType.slice(1) : 'Not specified'}
              />

              {tributeDonationsInfo.tributeDetails && (
                <InfoItem label="Tribute Details" value={tributeDonationsInfo.tributeDetails} />
              )}

              {tributeDonationsInfo.specialInstructions && (
                <InfoItem label="Special Instructions" value={tributeDonationsInfo.specialInstructions} />
              )}

              {tributeDonationsInfo.notes && (
                <InfoItem label="Notes" value={tributeDonationsInfo.notes} />
              )}

              {tributeDonationsInfo.estimatedCost && (
                <InfoItem label="Estimated Cost" value={`$${tributeDonationsInfo.estimatedCost.toLocaleString()}`} />
              )}

              {tributeDonationsInfo.contactName && (
                <InfoItem label="Contact Name" value={tributeDonationsInfo.contactName} />
              )}

              {tributeDonationsInfo.contactPhone && (
                <InfoItem label="Contact Phone" value={tributeDonationsInfo.contactPhone} />
              )}

              {tributeDonationsInfo.contactEmail && (
                <InfoItem label="Contact Email" value={tributeDonationsInfo.contactEmail} />
              )}

              {tributeDonationsInfo.organizationName && (
                <InfoItem label="Organization Name" value={tributeDonationsInfo.organizationName} />
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No tribute donations information found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Tribute Donations</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default TributeDonationsDataScreen;